import { create } from "zustand";
import type { Spread, Tier } from "@/types/spread";
import { spreadUid } from "@/types/spread";

export type Direction = "all" | "cex-cex" | "cex-dex" | "dex-dex";
export type MarketFilter = "all" | "spot-spot" | "spot-fut" | "fut-spot" | "fut-fut";

/** Per-row client-side metadata that the backend snapshot doesn't carry.
 *
 * Tracked across snapshots so the UI can:
 *   * Render ghost rows for spreads that vanished from the latest
 *     analyzer snapshot ("closed") — fade them for ``GHOST_TTL_MS`` so
 *     the operator has a chance to see what just blinked instead of
 *     the row disappearing mid-glance.
 *   * Show the peak spread % a row reached while it was live — useful
 *     to judge whether a fleeting opportunity was just a touch or a
 *     real multi-percent window.
 *   * Report how long it lived (first-seen → closed) in the closed
 *     view.
 */
export interface RowMeta {
  firstSeenAt: number;       // ms — when the UI first saw this UID
  peakSpreadPct: number;     // max net_spread_pct observed while live
  closedAt: number | null;   // ms — set the cycle the row disappeared from snapshot
}

/** Ghosts older than this are evicted on the next snapshot. 30 s gives
 *  the operator enough time to click the row for the detail panel. */
export const GHOST_TTL_MS = 30_000;

interface FiltersState {
  search: string;
  dir: Direction;
  market: MarketFilter;
  hedgeOnly: boolean;
  maxAgeMin: number | null;
  hideBlocked: boolean;
  // Include set. Empty = all. 1 selected = its spreads with every other venue;
  // ≥2 selected = ONLY spreads BETWEEN the selected venues (both legs in set).
  exchanges: string[];
  // Exclude set — hide any row where either leg is one of these venues.
  excludedExchanges: string[];
  // Active asset classes (#58): empty = all shown. Non-empty keeps only
  // rows whose ``asset_class`` (crypto when the field is absent) is listed.
  assetClasses: string[];
  // Active tiers (empty array = all 4 tiers shown). Non-empty keeps
  // only rows whose ``tier`` is in the list — lets the operator say
  // e.g. "only Very High + High" by clicking the two chips.
  tiers: Tier[];
  /** Minimum net-spread % to display, or ``null`` for no filter.
   *  Applied at entity-group level — a symbol group is shown if at
   *  least ONE of its rows has ``net_spread_pct >= minSpreadPct``.
   *  Set via single-click on any spread % cell in ``SpreadCell``;
   *  re-clicking the same value clears it (toggle). Also shows as a
   *  removable chip in ``FilterBar``. */
  minSpreadPct: number | null;
  /** Minimum net-profit USD to display, same semantics as
   *  ``minSpreadPct``. Set via click on the ``+$N net`` line in
   *  ``SpreadCell``. */
  minProfitUsd: number | null;
  /** When ``true`` the /spreads table shows ONLY rows whose backend
   *  ``tradfi_group`` field is set (CL/OIL, HK50, JP225, SPX500, …);
   *  when ``false`` (default) those rows are hidden so the operator's
   *  crypto-only view stays uncluttered. Toggled by the ``TradFi``
   *  button in ``FilterBar``. Bisects the same upstream payload that
   *  the backend now ships unified (crypto + tradfi merged into
   *  ``/ws/spreads`` after the 2026-05-19 unification). */
  tradfiOnly: boolean;
  selectedUid: string | null;
  expandedSymbols: string[]; // symbols shown expanded beyond top-3
}

/** Which column drives the symbol-group sort, or ``null`` to fall back
 *  to the default (search-rank + best-profit DESC). The sort applies
 *  across all tier buckets — each bucket's symbols are re-ordered by
 *  ``maxSpreadPct`` or ``maxProfitUsd`` (group-level aggregate). */
export type SortColumn = "spread" | "profit" | null;
/** ``null`` direction means no sort active (the column is "off"). */
export type SortDir = "asc" | "desc" | null;

interface SpreadsState {
  rows: Map<string, Spread>;
  /** Client-side metadata parallel to ``rows`` (same keys). See ``RowMeta``. */
  meta: Map<string, RowMeta>;
  lastSnapshotAt: number;
  connected: boolean;

  filters: FiltersState;

  /** Current sort column for symbol groups, or ``null`` for default
   *  (search-rank + best-profit DESC). Driven by clicks on the
   *  ``Spread`` / ``Profit`` subheaders in ``SpreadTable``. */
  sortBy: SortColumn;
  /** Direction of the current sort, ``null`` when ``sortBy`` is ``null``. */
  sortDir: SortDir;

  applySnapshot: (rows: Spread[]) => void;
  applyDelta: (updated: Spread[], deleted: string[]) => void;
  /** Update a ghost row's frozen prices with a fresh top-of-book pull —
   *  called once per row after it first transitions to ghost, so the
   *  operator sees the state of the market at (or near) the moment the
   *  spread converged, not the last-but-one analyzer tick. */
  updateGhostPrices: (uid: string, buyPrice: number, sellPrice: number) => void;
  setConnected: (c: boolean) => void;
  setFilter: <K extends keyof FiltersState>(k: K, v: FiltersState[K]) => void;
  resetFilters: () => void;
  toggleExchange: (ex: string) => void;
  // Tri-state chip cycle: neutral → include → exclude → neutral.
  cycleExchange: (ex: string) => void;
  toggleTier: (tier: Tier) => void;
  toggleAssetClass: (ac: string) => void;
  toggleSymbolExpanded: (sym: string) => void;
  /** Single-click toggle on a spread/profit cell. Sets the matching
   *  ``minSpreadPct`` / ``minProfitUsd`` filter to ``value``; if the
   *  same value is already active, clears it (toggle off). Used by
   *  ``SpreadCell`` cell-clicks and ``FilterBar`` chip X-buttons (the
   *  latter passes ``null``). */
  toggleMinSpread: (value: number | null) => void;
  toggleMinProfit: (value: number | null) => void;
  /** Cycle the sort state for a column: none → desc → asc → none.
   *  Switching to a different column resets to its first state (desc).
   *  Mirrors the standard table-header sort UX. */
  cycleSort: (column: "spread" | "profit") => void;
}

// Defaults chosen to put the operator straight on the working set:
//   dir="all"           — unfiltered across CEX/DEX directions
//   market="all"        — unfiltered across spot/futures
//   maxAgeMin=5         — recency cutoff (5 min); stale signals stay out of
//                         the default view
//   hideBlocked=true    — "Actionable" toggle ON by default; signals whose
//                         transfer rail is fully blocked don't pollute the
//                         main table (user can flip the toggle off to see
//                         them). Matches the "Actionable" pre-selection
//                         requested after seeing too many dead-route rows
//                         while screen-scanning.
const initialFilters: FiltersState = {
  search: "",
  dir: "all",
  market: "all",
  hedgeOnly: false,
  maxAgeMin: 5,
  hideBlocked: true,
  exchanges: [],
  excludedExchanges: [],
  assetClasses: [],  // empty = all asset classes visible
  tiers: [],  // empty = all tiers visible
  minSpreadPct: null,
  minProfitUsd: null,
  tradfiOnly: false,
  selectedUid: null,
  expandedSymbols: [],
};

/**
 * Shallow compare spreads by the fields that change per cycle. Price / pct /
 * volume / stale / funding update frequently; the rest (asset, exchanges,
 * markets, networks) usually don't. Keep this list tight — every compared
 * field is a chance for a cheap reference preservation.
 */
function sameSpread(a: Spread, b: Spread): boolean {
  return (
    a.buy_price === b.buy_price &&
    a.sell_price === b.sell_price &&
    a.top_buy_price === b.top_buy_price &&
    a.top_sell_price === b.top_sell_price &&
    a.net_spread_pct === b.net_spread_pct &&
    a.net_profit_usd === b.net_profit_usd &&
    a.volume_usd === b.volume_usd &&
    a.stale === b.stale &&
    a.tier === b.tier &&
    a.first_seen_at === b.first_seen_at &&
    a.funding_buy === b.funding_buy &&
    a.funding_sell === b.funding_sell
  );
}

export const useSpreadsStore = create<SpreadsState>()((set) => ({
  rows: new Map(),
  meta: new Map(),
  lastSnapshotAt: 0,
  connected: false,
  filters: initialFilters,
  sortBy: null,
  sortDir: null,

  applySnapshot: (rows) =>
    set((s) => {
      const now = Date.now();
      const nextRows = new Map<string, Spread>();
      const nextMeta = new Map<string, RowMeta>();
      const incomingUids = new Set<string>();

      // 1) Snapshot arrivals: update row + refresh / create meta. Peak
      //    pct is a running max so we can surface "this row hit 12% at
      //    one point" even if the current cycle shows 2%.
      for (const r of rows) {
        const uid = spreadUid(r);
        incomingUids.add(uid);
        const prevRow = s.rows.get(uid);
        const prevMeta = s.meta.get(uid);
        // Preserve reference if nothing that affects rendering changed.
        // Critical for React.memo on SpreadRow — a new object identity
        // would force re-render of every row on every snapshot.
        nextRows.set(uid, prevRow && sameSpread(prevRow, r) ? prevRow : r);
        const curPct = Number(r.net_spread_pct ?? 0);
        nextMeta.set(uid, {
          firstSeenAt: prevMeta?.firstSeenAt ?? now,
          peakSpreadPct: Math.max(prevMeta?.peakSpreadPct ?? 0, curPct),
          closedAt: null,  // row is alive this cycle — clears any previous ghost mark
        });
      }

      // 2) Ghosts: rows that were in our map last cycle but vanished
      //    now. Stamp ``closedAt`` (first time we noticed) and keep
      //    them rendered — faded — until the TTL expires. Rows older
      //    than ``GHOST_TTL_MS`` since disappearance are evicted.
      for (const [uid, oldRow] of s.rows) {
        if (incomingUids.has(uid)) continue;
        const oldMeta = s.meta.get(uid);
        const closedAt = oldMeta?.closedAt ?? now;
        if (now - closedAt > GHOST_TTL_MS) continue;  // expired, drop
        nextRows.set(uid, oldRow);
        nextMeta.set(uid, {
          firstSeenAt: oldMeta?.firstSeenAt ?? now,
          peakSpreadPct: oldMeta?.peakSpreadPct ?? Number(oldRow.net_spread_pct ?? 0),
          closedAt,
        });
      }

      return { rows: nextRows, meta: nextMeta, lastSnapshotAt: now };
    }),

  // Delta path (#85): merge changed rows + ghost-mark vanished uids in place,
  // leaving unchanged rows (and their references) untouched. Mirrors the ghost
  // logic of applySnapshot so closed spreads still fade for GHOST_TTL_MS.
  applyDelta: (updated, deleted) =>
    set((s) => {
      const now = Date.now();
      const nextRows = new Map(s.rows);
      const nextMeta = new Map(s.meta);

      // 1) Changed rows → refresh row + meta (alive, clears any ghost mark).
      for (const r of updated) {
        const uid = spreadUid(r);
        const prevRow = s.rows.get(uid);
        const prevMeta = s.meta.get(uid);
        nextRows.set(uid, prevRow && sameSpread(prevRow, r) ? prevRow : r);
        const curPct = Number(r.net_spread_pct ?? 0);
        nextMeta.set(uid, {
          firstSeenAt: prevMeta?.firstSeenAt ?? now,
          peakSpreadPct: Math.max(prevMeta?.peakSpreadPct ?? 0, curPct),
          closedAt: null,
        });
      }

      // 2) Vanished uids → ghost-mark (keep faded until TTL).
      for (const uid of deleted) {
        const oldRow = nextRows.get(uid);
        if (!oldRow) continue;
        const oldMeta = nextMeta.get(uid);
        if (oldMeta?.closedAt == null) {
          nextMeta.set(uid, {
            firstSeenAt: oldMeta?.firstSeenAt ?? now,
            peakSpreadPct: oldMeta?.peakSpreadPct ?? Number(oldRow.net_spread_pct ?? 0),
            closedAt: now,
          });
        }
      }

      // 3) Evict ghosts past the TTL (snapshot did this implicitly by omission).
      for (const [uid, m] of nextMeta) {
        if (m.closedAt != null && now - m.closedAt > GHOST_TTL_MS) {
          nextRows.delete(uid);
          nextMeta.delete(uid);
        }
      }

      return { rows: nextRows, meta: nextMeta, lastSnapshotAt: now };
    }),

  updateGhostPrices: (uid, buyPrice, sellPrice) =>
    set((s) => {
      const row = s.rows.get(uid);
      if (!row) return {};
      // No-op if the caller came back with identical numbers — keeps
      // reference equality, which keeps React.memo happy and avoids a
      // cascade of unnecessary re-renders across the list.
      if (row.buy_price === buyPrice && row.sell_price === sellPrice) return {};
      // Recompute the displayed spread so the "closed — peak Y% — lived
      // Ns" badge reads the convergence point accurately. We compute the
      // gross pct here (no fees re-deducted) — good enough for a ghost
      // label, and avoids threading fee maps to the client.
      const grossPct =
        buyPrice > 0 ? ((sellPrice - buyPrice) / buyPrice) * 100 : row.net_spread_pct;
      const nextRows = new Map(s.rows);
      nextRows.set(uid, {
        ...row,
        buy_price: buyPrice,
        sell_price: sellPrice,
        net_spread_pct: grossPct,
      });
      return { rows: nextRows };
    }),

  setConnected: (connected) => set(() => ({ connected })),

  setFilter: (k, v) =>
    set((s) => ({ filters: { ...s.filters, [k]: v } })),

  resetFilters: () => set(() => ({ filters: initialFilters })),

  toggleExchange: (ex) =>
    set((s) => {
      const cur = new Set(s.filters.exchanges);
      if (cur.has(ex)) cur.delete(ex);
      else cur.add(ex);
      return { filters: { ...s.filters, exchanges: [...cur] } };
    }),

  cycleExchange: (ex) =>
    set((s) => {
      const inc = new Set(s.filters.exchanges);
      const exc = new Set(s.filters.excludedExchanges);
      // neutral → include → exclude → neutral
      if (inc.has(ex)) {
        inc.delete(ex);
        exc.add(ex);
      } else if (exc.has(ex)) {
        exc.delete(ex);
      } else {
        inc.add(ex);
      }
      return {
        filters: { ...s.filters, exchanges: [...inc], excludedExchanges: [...exc] },
      };
    }),

  toggleTier: (tier) =>
    set((s) => {
      const cur = new Set(s.filters.tiers);
      if (cur.has(tier)) cur.delete(tier);
      else cur.add(tier);
      return { filters: { ...s.filters, tiers: [...cur] } };
    }),

  toggleAssetClass: (ac) =>
    set((s) => {
      const cur = new Set(s.filters.assetClasses);
      if (cur.has(ac)) cur.delete(ac);
      else cur.add(ac);
      return { filters: { ...s.filters, assetClasses: [...cur] } };
    }),

  toggleSymbolExpanded: (sym) =>
    set((s) => {
      const cur = new Set(s.filters.expandedSymbols);
      if (cur.has(sym)) cur.delete(sym);
      else cur.add(sym);
      return { filters: { ...s.filters, expandedSymbols: [...cur] } };
    }),

  toggleMinSpread: (value) =>
    set((s) => {
      // Re-clicking the same value clears (toggle). Explicit ``null``
      // from the chip X-button also clears. Float-equality via tolerant
      // diff so a click on "15.6%" (cell snapshot) and the previous
      // store value of 15.5999… match.
      const cur = s.filters.minSpreadPct;
      const next =
        value === null
          ? null
          : cur !== null && Math.abs(cur - value) < 1e-6
            ? null
            : value;
      return { filters: { ...s.filters, minSpreadPct: next } };
    }),

  toggleMinProfit: (value) =>
    set((s) => {
      const cur = s.filters.minProfitUsd;
      const next =
        value === null
          ? null
          : cur !== null && Math.abs(cur - value) < 1e-6
            ? null
            : value;
      return { filters: { ...s.filters, minProfitUsd: next } };
    }),

  cycleSort: (column) =>
    set((s) => {
      // Standard table-header sort UX: switching to a fresh column
      // starts at ``desc`` (most-interesting on top — biggest spread /
      // biggest profit). Cycling on the active column goes
      // desc → asc → off.
      if (s.sortBy !== column) {
        return { sortBy: column, sortDir: "desc" };
      }
      if (s.sortDir === "desc") {
        return { sortBy: column, sortDir: "asc" };
      }
      // ``asc`` → off
      return { sortBy: null, sortDir: null };
    }),
}));

export function selectRows(s: SpreadsState): Map<string, Spread> {
  return s.rows;
}

export function selectFilters(s: SpreadsState): FiltersState {
  return s.filters;
}

export function tierKey(t: Tier | undefined): number {
  switch (t) {
    case "vh":
      return 0;
    case "h":
      return 1;
    case "med":
      return 2;
    default:
      return 3;
  }
}
